import { parseCurrencyInputToCents } from './refundRecommendation';

/** Matches the backend `supervisorNameSchema` cap (the name is appended to the Maxio memo). */
export const REFUND_SUPERVISOR_NAME_MAX_LENGTH = 100;

export const REFUND_SUPERVISOR_APPROVAL_REQUIRED_CODE = 'REFUND_SUPERVISOR_APPROVAL_REQUIRED';

/** Mirrors `SupervisorApprovalRequiredReason`, sent with `REFUND_SUPERVISOR_APPROVAL_REQUIRED`. */
export type RefundSupervisorApprovalReason =
  'AMOUNT_INDETERMINATE' | 'OVER_THRESHOLD' | 'THRESHOLD_UNAVAILABLE';

/** The backend reasons, plus `THRESHOLD_NOT_LOADED` when the form itself cannot read the threshold. */
export type RefundSupervisorApprovalPromptReason =
  RefundSupervisorApprovalReason | 'THRESHOLD_NOT_LOADED';

export type RefundSupervisorApprovalRejection = {
  reason: RefundSupervisorApprovalReason | undefined;
};

/**
 * The threshold read. `unavailable`: the backend found no usable setting (missing, cleared or
 * invalid), which its gate treats as $0. `not-loaded`: the read failed or returned something else.
 */
export type RefundSupervisorThreshold =
  { cents: number; status: 'loaded' } | { status: 'not-loaded' } | { status: 'unavailable' };

const supervisorApprovalReasons: RefundSupervisorApprovalReason[] = [
  'AMOUNT_INDETERMINATE',
  'OVER_THRESHOLD',
  'THRESHOLD_UNAVAILABLE'
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Read the `GET /refund-supervisor-approval-threshold` response.
 *
 * Why:
 * The backend converts the setting (dollars, what Finance types) into cents with the parser its
 * gate uses, so the form and the gate cannot disagree about a cleared or invalid value.
 *
 * How:
 * Used as the query's `transformResponse`. Anything but whole, non-negative cents or `null`
 * reads as `not-loaded`, so the form asks for a supervisor by default.
 */
export const readRefundSupervisorThreshold = (response: unknown): RefundSupervisorThreshold => {
  const thresholdCents =
    isRecord(response) && isRecord(response.data) ? response.data.thresholdCents : undefined;

  if (thresholdCents === null) return { status: 'unavailable' };
  if (
    typeof thresholdCents === 'number' &&
    Number.isInteger(thresholdCents) &&
    thresholdCents >= 0
  ) {
    return { cents: thresholdCents, status: 'loaded' };
  }

  return { status: 'not-loaded' };
};

/**
 * Decides whether the form asks for a supervisor before submitting, and why. `undefined`
 * means no approval is needed. The threshold is exclusive: a refund of exactly the threshold
 * needs no approval.
 *
 * A threshold or amount the form cannot establish asks by default, the same way the backend
 * gate fails closed.
 */
export const getRefundSupervisorApprovalPromptReason = (
  amountCents: number | undefined,
  threshold: RefundSupervisorThreshold
): RefundSupervisorApprovalPromptReason | undefined => {
  if (threshold.status === 'not-loaded') return 'THRESHOLD_NOT_LOADED';
  if (threshold.status === 'unavailable') return 'THRESHOLD_UNAVAILABLE';
  if (amountCents === undefined) return 'AMOUNT_INDETERMINATE';

  return amountCents > threshold.cents ? 'OVER_THRESHOLD' : undefined;
};

/**
 * Total, in cents, of every refund one cancellation, downgrade or deactivation will issue.
 *
 * The backend gate (`requireSupervisorApprovalForTotal`) measures the whole action, so a refund
 * split across programs cannot slip under the threshold. The prompt has to measure the same
 * total. `undefined` when any amount cannot be read, so the prompt asks by default, the same way
 * the backend fails closed.
 *
 * Example: getRefundTotalCents([85, '12.5']) -> 9750
 */
export const getRefundTotalCents = (amounts: (number | string)[]): number | undefined => {
  let totalCents = 0;

  for (const amount of amounts) {
    const cents = parseCurrencyInputToCents(String(amount));

    if (cents === null) return undefined;
    totalCents += cents;
  }

  return totalCents;
};

/**
 * Trim and cap the approving supervisor name the way the refund schema does.
 * Blank and over-length values collapse to `undefined` so they are never sent.
 */
export const normalizeSupervisorName = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim();

  if (
    trimmed === undefined ||
    trimmed === '' ||
    trimmed.length > REFUND_SUPERVISOR_NAME_MAX_LENGTH
  ) {
    return undefined;
  }

  return trimmed;
};

const readExecutionDetails = (value: unknown, depth = 0): Record<string, unknown> | undefined => {
  if (depth > 4 || !isRecord(value)) return undefined;
  if (typeof value.executionCode === 'string') return value;
  if ('data' in value) return readExecutionDetails(value.data, depth + 1);

  return undefined;
};

const isRefundSupervisorApprovalReason = (
  value: unknown
): value is RefundSupervisorApprovalReason =>
  supervisorApprovalReasons.some((reason) => reason === value);

/**
 * Reads the refund API's rejection for a missing supervisor name, with the backend's reason.
 * Returns `undefined` for any other error. An unrecognized reason is kept as `undefined` so
 * the popup falls back to its generic copy.
 */
export const readRefundSupervisorApprovalRejection = (
  error: unknown
): RefundSupervisorApprovalRejection | undefined => {
  const details = readExecutionDetails(error);

  if (details?.executionCode !== REFUND_SUPERVISOR_APPROVAL_REQUIRED_CODE) return undefined;

  return { reason: isRefundSupervisorApprovalReason(details.reason) ? details.reason : undefined };
};
